import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { useIntl } from "@ai360/core/intl";

import {
    Button,
    Section,
    SelectInput,
    SubSection,
    LoaderTypes,
    Loader,
    NumericInput,
    DialogBoxFooterType,
    DialogBox,
    Checkbox,
} from "~/core";
import {
    AgBytesAPI,
    AgEventAPI,
    Ai360API,
    DefinedLegendAPI,
    HierarchyAPI,
    ISelectOption,
    LayerAPI,
    PicklistAPI,
    SurfaceDefaultsAPI,
} from "@ai360/core";
import * as layerSelectors from "~/action-panel/components/layer-module/components/layer-list/selectors";
import {
    colorOptionRenderer,
    getBackgroundGradientStyle,
} from "~/action-panel/components/layer-module/utils";
import { AgBytesItem } from "~/action-panel/components/event-module/components/event-info/components/event-scouting/models";
import {
    computeIsSelectable,
    IS_SELECTABLE,
    useLoggedInUserProductScopeOrgLevelGuid,
    useProductList,
} from "~/admin/setup/customer/use-product-list";
import { getTheUserGuid } from "~/login/selectors";

import { IOption } from "~/core/components/select-input/model";
import { ButtonTypes } from "~/core/components/button/button";
import SlidingPanel from "~/sliding-panel/sliding-panel";
import { OrgLevelList } from "../../org-level-products/add-edit/components/org-level-list";

import { ACTIVE_YN, picklistNames } from "~/core/picklist";
import { model } from "../data";

import { resetApiResult } from "~/core/api/actions";
import { messages } from "../../defined-legend/i18n-messages";
import { messages as globalMessages } from "~/i18n-messages";

import "../../add-edit-shared-responsive.css";
import "./add-edit-panel.css";
import { clearAllOwnerDefinedLegends } from "~/action-panel/components/layer-module/components/layer-list/actions";
import { PARENT_SURFACE_DEFAULT_ERROR_CODE } from "../defined-legend";

type IDefinedLegendRow = DefinedLegendAPI.IDefinedLegendGridRow;

type IHierarchyFilterLevelWithParentsGuid = HierarchyAPI.IHierarchyFilterLevelWithParentsGuid;
type IAgBytesImportAttribute = AgBytesAPI.IAgBytesImportAttribute;
type IImportAttribute = DefinedLegendAPI.IImportAttribute;
type IPicklistValue = PicklistAPI.IPicklistValue;
type IListItem = SurfaceDefaultsAPI.IListItem;

enum LayerType {
    Application = "Application",
    ApplicationRec = "Application Rec",
    Harvest = "Harvest",
    Planting = "Planting",
    PlantingRec = "Planting Rec",
}

interface IDefinedLegendAddEditPanelProps {
    closePanel: () => void;
    selectedRow: IDefinedLegendRow | null;
    data: {
        onSave: () => void;
    };
}

const EMPTY_LOCATION_DATA = { model: [] as IHierarchyFilterLevelWithParentsGuid[] };
const EMPTY_ATTRIBUTE_DATA = { results: [] as IImportAttribute[] };
const EMPTY_CROP_DATA = { model: [] as IAgBytesImportAttribute[] };
const EMPTY_CROP_PURPOSE_DATA = { model: [] as IPicklistValue[] };
const EMPTY_DISPLAY_TYPE_DATA = { model: [] as IListItem[] };

const DefinedLegendAddEditPanel = ({
    closePanel,
    selectedRow,
}: IDefinedLegendAddEditPanelProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const [draftRecord, onRecordChange] = useState(selectedRow || {});
    const [isAdd, setIsAdd] = useState(false);
    const [isRec, setIsRec] = useState(false);
    const userGuid = useAppSelector(getTheUserGuid);

    const isApplicationLayerType = [LayerType.ApplicationRec, LayerType.Application].includes(
        draftRecord[model.PROPS_LAYER_TYPE_NAME]
    );

    const [selectedLocations, setSelectedLocations] = useState(
        [] as HierarchyAPI.IHierarchyFilterLevelWithParentsGuid[]
    );

    const loggedInUserProductScopeOrgLevelGuid = useLoggedInUserProductScopeOrgLevelGuid();
    // Once the user picks a Location Affiliation below, filter against that location instead of
    // the logged-in scope -- the Owner-level fallback never has its own Agvance credentials.
    const productScopeOrgLevelGuid =
        selectedLocations[0]?.orgLevelGuid ?? loggedInUserProductScopeOrgLevelGuid;
    const { products: allProducts, isFetching: isProductListLoading } = useProductList({
        orgLevelGuid: productScopeOrgLevelGuid,
        scope: AgBytesAPI.ProductSelectionListScope.UserHierarchy,
    });

    const numberOfClassesOptions = useAppSelector(layerSelectors.getNumberOfClasses);
    const colorRamps: LayerAPI.IColorRamp[] = useAppSelector(layerSelectors.getColorRamps);
    const [selectedColorRampGuid, setSelectedColorRampGuid] = useState<string>(
        selectedRow ? selectedRow[model.PROPS_COLOR_RAMP_GUID] : null
    );
    const [colorRampSelectOptions, setColorRampSelectOptions] = useState<
        { background: string; label: string; value: string }[]
    >([]);
    const [backgroundImageStyle, setBackgroundImageStyle] = useState<string>();
    const [numberOfClasses, setNumberOfClasses] = useState<number>(
        selectedRow ? selectedRow[model.PROPS_CLASSBREAKS]?.length : null
    );
    const [createSurfaceDefault, setCreateSurfaceDefault] = useState<boolean>(false);
    const [dlClassBreaks, setDlClassBreaks] = useState<LayerAPI.IClassBreak[]>([]);
    const [classBreakValidationError, setClassBreakValidationError] = useState<string>(null);
    const [surfaceDefaultValidationDialog, setSurfaceDefaultValidationDialog] = useState<{
        isOpen: boolean;
        message: string;
    }>({
        isOpen: false,
        message: "",
    });
    const pendingDefinedLegendModelRef = useRef<DefinedLegendAPI.IMergeDefinedLegendRequest | null>(
        null
    );

    const [addDefinedLegend, { isLoading: isAddLoading }] =
        DefinedLegendAPI.useAddDefinedLegendMutation();

    const [updateDefinedLegend, { isLoading: isUpdateLoading }] =
        DefinedLegendAPI.useUpdateDefinedLegendMutation();
    const [validateDefinedLegend, { isLoading: isValidateLoading }] =
        DefinedLegendAPI.useValidateDefinedLegendMutation();

    const { data: locationData = EMPTY_LOCATION_DATA, isFetching: isLocationLoading } =
        HierarchyAPI.useGetSetupOrgLevelListWithParentsGuidQuery("_", {
            skip: !userGuid,
        });

    const generateOptions = (
        list: (
            | AgBytesItem
            | IImportAttribute
            | IAgBytesImportAttribute
            | IPicklistValue
            | AgEventAPI.IAvailableProduct
            | SurfaceDefaultsAPI.IListItem
        )[],
        labelKey: string,
        valueKey: string,
        sort: boolean = false
    ): IOption[] => {
        if (list && list.length > 0) {
            if (sort) {
                list = [...list].sort((a, b) => a[labelKey].localeCompare(b[labelKey]));
            }
            return list.map((item) => {
                return { label: item[labelKey], value: item[valueKey] };
            });
        }
        return [];
    };

    const getAttributeType = (layerTypeName: string): string => {
        switch (layerTypeName) {
            case LayerType.Application:
            case LayerType.ApplicationRec:
                return LayerType.Application;
            case LayerType.Planting:
            case LayerType.PlantingRec:
                return LayerType.Planting;
            case LayerType.Harvest:
                return LayerType.Harvest;
            default:
                return null;
        }
    };

    const layerTypeOptions = useMemo(
        () =>
            Object.values(LayerType)
                .filter((layerType) => !layerType.toLowerCase().includes("rec")) // Temporary Filter
                .map((layerType) => ({
                    label: layerType,
                    value: layerType,
                })),
        [LayerType]
    );

    const { data: attributeData = EMPTY_ATTRIBUTE_DATA, isFetching: attributeIsLoading } =
        DefinedLegendAPI.useGetImportAttributesQuery(null, {
            skip: !userGuid,
        });

    const { data: cropData = EMPTY_CROP_DATA, isFetching: cropDataIsLoading } =
        AgBytesAPI.useGetCropDropdownListQuery(userGuid, {
            skip: !userGuid,
        });

    const { data: displayTypeData = EMPTY_DISPLAY_TYPE_DATA, isFetching: displayTypesLoading } =
        SurfaceDefaultsAPI.useGetDisplayTypeListQuery(
            { model: "Name", userGuid },
            {
                skip: !userGuid,
            }
        );

    const {
        data: cropPurposeData = EMPTY_CROP_PURPOSE_DATA,
        isFetching: cropPurposeDataIsLoading,
    } = DefinedLegendAPI.useGetPicklistValuesByIdQuery(
        picklistNames.getPickListCode(picklistNames.PICKLIST_CROP_PURPOSE),
        {
            skip: !userGuid,
        }
    );

    const cropOptions = useMemo(
        () =>
            cropData
                ? generateOptions(cropData.model, model.PROPS_CROP_NAME, model.PROPS_CROP_GUID)
                : [],
        [cropData]
    );

    const cropPurposeOptions = useMemo(
        () =>
            cropPurposeData
                ? generateOptions(
                      cropPurposeData.model,
                      model.PROPS_CROP_PURPOSE_NAME,
                      model.PROPS_CROP_PURPOSE_PICKLIST_GUID
                  )
                : [],
        [cropPurposeData]
    );

    const attributeOptions = useMemo(
        () =>
            attributeData.results.length
                ? generateOptions(
                      attributeData.results?.filter(
                          (attr: IImportAttribute) =>
                              attr[model.PROPS_LAYER_TYPE_NAME] ===
                              getAttributeType(draftRecord[model.PROPS_LAYER_TYPE_NAME])
                      ),
                      model.PROPS_ATTRIBUTE_NAME,
                      model.PROPS_ATTRIBUTE_GUID,
                      true
                  )
                : [],
        [attributeData, draftRecord[model.PROPS_LAYER_TYPE_NAME]]
    );

    const selectedProductGuid = draftRecord[model.PROPS_PRODUCT_GUID];
    const productOptions = useMemo(
        () =>
            allProducts.map((product) => ({
                activeYn: product.activeYn,
                isSelectable: computeIsSelectable(product.activeYn, product.isAgvanceLinked),
                label: product.name,
                value: product.productGuid,
            })),
        [allProducts]
    );
    const isSelectedProductUnlinked = useMemo(() => {
        const selectedProduct = allProducts.find(
            (product) => product.productGuid === selectedProductGuid
        );
        return (
            Boolean(selectedProductGuid) &&
            selectedProduct != null &&
            selectedProduct.isAgvanceLinked === false
        );
    }, [allProducts, selectedProductGuid]);

    const productNotLinkedText = isSelectedProductUnlinked
        ? formatMessage(globalMessages.productNotLinkedInAgvance)
        : undefined;

    const displayTypeOptions = useMemo(
        () =>
            displayTypeData
                ? generateOptions(
                      displayTypeData.model,
                      model.PROPS_DISPLAY_TYPE_NAME,
                      model.PROPS_DISPLAY_TYPE_GUID
                  )
                : [],
        [displayTypeData, isRec]
    );

    const updateDefinedLegendClassBreak = useCallback((classId: number, value: number | null) => {
        if (value == null || Number.isNaN(value)) {
            return;
        }

        setDlClassBreaks((previousClassBreaks) => {
            const currentClassBreak = previousClassBreaks.find((cb) => cb.classId === classId);
            if (!currentClassBreak || currentClassBreak.upperBound === value) {
                return previousClassBreaks;
            }

            return previousClassBreaks.map((cb) => {
                if (cb.classId === classId) {
                    return {
                        ...cb,
                        upperBound: value,
                    };
                }
                if (cb.classId === classId + 1) {
                    return {
                        ...cb,
                        lowerBound: value,
                    };
                }
                return cb;
            });
        });
    }, []);

    const parseNumericInputValue = useCallback((value?: string): number | null => {
        if (value == null) {
            return null;
        }

        const normalizedValue = value.replace(/,/g, "").trim();
        if (normalizedValue === "") {
            return null;
        }

        const parsedValue = Number(normalizedValue);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    }, []);

    const legendPreviewItems = useMemo(() => {
        if (!dlClassBreaks || dlClassBreaks.length === 0) {
            return;
        }

        return dlClassBreaks.map((classBreak, index) => {
            const isLastClassBreak = index === numberOfClasses - 1;
            const lowerBoundValue = index === 0 ? "Min" : dlClassBreaks[index].lowerBound ?? "";
            const upperBoundValue = isLastClassBreak
                ? "Max"
                : dlClassBreaks[index].upperBound ?? "";

            return (
                <div key={classBreak.classId} className="legend-preview-item">
                    <div
                        className="legend-icon"
                        style={{ backgroundColor: `#${classBreak.color.hexCode}` }}
                    />
                    <div className="legend-label">{classBreak.classId}</div>
                    <div className="legend-class-min">{lowerBoundValue}</div>
                    <div className="legend-class-min">{" - "}</div>
                    <div className="legend-class-max">
                        {isLastClassBreak ? (
                            "Max"
                        ) : (
                            <NumericInput
                                scale={2}
                                precision={9}
                                onBlur={(event) =>
                                    updateDefinedLegendClassBreak(
                                        classBreak.classId,
                                        parseNumericInputValue(event?.target?.value)
                                    )
                                }
                                value={upperBoundValue.toString()}
                                useValueTitle={true}
                                showTopLabel={false}
                                defaultValue={""}
                                maxlength={9}
                                suppressError={true}
                                testId={`defined-legend-class-break-upper-bound-input-${classBreak.classId}`}
                            />
                        )}
                    </div>
                </div>
            );
        });
    }, [numberOfClasses, dlClassBreaks, updateDefinedLegendClassBreak, parseNumericInputValue]);

    const numClassesOptions = useMemo(
        () =>
            numberOfClassesOptions?.map(
                (noc) =>
                    ({
                        label: noc.toString(),
                        value: noc,
                    } as ISelectOption<number>)
            ) ?? [],
        [numberOfClassesOptions]
    );

    const mutableLocationData = useMemo(() => {
        return locationData.model.map((location) => ({ ...location }));
    }, [locationData]);

    const locationOptions = useMemo(() => {
        const locationGuids = new Set<string>(
            mutableLocationData.map((location) => location.orgLevelGuid)
        );
        const missingFromSelected = selectedLocations.filter((location) => {
            return !locationGuids.has(location.orgLevelGuid);
        });
        return [...missingFromSelected, ...mutableLocationData];
    }, [selectedLocations, mutableLocationData]);

    useEffect(() => {
        if (!selectedRow) {
            setIsAdd(true);
        }
        return () => {
            dispatch(Ai360API.util.invalidateTags(["DefinedLegend"]));
            dispatch(clearAllOwnerDefinedLegends());
        };
    }, []);

    useEffect(() => {
        if (isApplicationLayerType) {
            onRecordChange({
                ...draftRecord,
                [model.PROPS_CROP_GUID]: null,
                [model.PROPS_CROP_PURPOSE_GUID]: null,
                [model.PROPS_ATTRIBUTE_GUID]: draftRecord[model.PROPS_ATTRIBUTE_GUID] ?? null,
            });
        } else {
            onRecordChange({
                ...draftRecord,
                [model.PROPS_PRODUCT_GUID]: null,
                [model.PROPS_ATTRIBUTE_GUID]: draftRecord[model.PROPS_ATTRIBUTE_GUID] ?? null,
            });
        }

        if (
            [LayerType.ApplicationRec, LayerType.PlantingRec].includes(
                draftRecord[model.PROPS_LAYER_TYPE_NAME]
            )
        ) {
            setIsRec(true);
        } else {
            setIsRec(false);
        }
    }, [draftRecord[model.PROPS_LAYER_TYPE_NAME]]);

    useEffect(() => {
        if (colorRamps && colorRampSelectOptions.length === 0) {
            const selectColorRamp = draftRecord[model.PROPS_COLOR_RAMP_GUID]
                ? colorRamps.find(
                      (cr) => cr.colorRampGuid === draftRecord[model.PROPS_COLOR_RAMP_GUID]
                  )
                : null;
            setColorRampSelectOptions(
                colorRamps.map((cr) => ({
                    background: getBackgroundGradientStyle(cr.layerFiles.slice(-1)[0]),
                    label: " ",
                    titleValue: cr.colorRampName,
                    value: cr.colorRampGuid,
                }))
            );
            if (selectColorRamp) {
                setBackgroundImageStyle(
                    getBackgroundGradientStyle(selectColorRamp?.layerFiles.slice(-1)[0])
                );
                setSelectedColorRampGuid(selectColorRamp?.colorRampGuid);
            }
        }
    }, [colorRamps]);

    useEffect(() => {
        if (!selectedColorRampGuid) {
            setDlClassBreaks([]);
            return;
        }
        const colorRampNew = colorRamps.find((cs) => cs.colorRampGuid === selectedColorRampGuid);
        setBackgroundImageStyle(
            colorRampNew && getBackgroundGradientStyle(colorRampNew.layerFiles.slice(-1)[0])
        );

        const layerFile =
            colorRampNew &&
            colorRampNew.layerFiles.find((f) => f.numberOfClasses === Number(numberOfClasses));

        setDlClassBreaks((previousClassBreaks) =>
            (layerFile?.classBreaks ?? []).map((cb, index) => {
                const oldClassBreak =
                    previousClassBreaks &&
                    previousClassBreaks.find((oldCb) => oldCb.classId === cb.classId);
                return {
                    classId: cb.classId,
                    displayName: cb.classId.toString(),
                    color: { ...cb.color } as LayerAPI.ILayerColor,
                    upperBound:
                        index === numberOfClasses - 1 ? "Max" : oldClassBreak?.upperBound ?? 0,
                    lowerBound: index === 0 ? "Min" : oldClassBreak?.lowerBound ?? 0,
                } as LayerAPI.IClassBreak;
            })
        );
    }, [selectedColorRampGuid, numberOfClasses, colorRamps]);

    useEffect(() => {
        if (!selectedRow || locationData?.model.length === 0 || !colorRamps) return;

        const selectedOrgLevels =
            selectedRow[model.PROPS_ORG_LEVELS] ?? selectedRow[model.PROPS_LOCATIONS] ?? [];
        const orgLevelGuids = new Set(selectedOrgLevels.map((l) => l[model.PROPS_ORG_LEVEL_GUID]));

        const locations = (locationData?.model ?? [])
            .filter((location) => orgLevelGuids.has(location.orgLevelGuid))
            .map((location) => ({ ...location }));
        setSelectedLocations(locations);

        if (selectedRow[model.PROPS_CLASSBREAKS]) {
            const rowNumberOfClasses = selectedRow[model.PROPS_CLASSBREAKS].length;
            const colorRampGuid = selectedRow[model.PROPS_COLOR_RAMP_GUID];
            const colorRampNew = colorRamps.find((cs) => cs.colorRampGuid === colorRampGuid);
            setBackgroundImageStyle(
                colorRampNew && getBackgroundGradientStyle(colorRampNew.layerFiles.slice(-1)[0])
            );

            const layerFile =
                colorRampNew &&
                colorRampNew.layerFiles.find(
                    (f) => f.numberOfClasses === Number(rowNumberOfClasses)
                );

            const rowClassBreaks = selectedRow[model.PROPS_CLASSBREAKS].map((cb, index) => {
                return {
                    classId: cb.classId,
                    displayName: cb.classId.toString(),
                    color: { ...layerFile?.classBreaks[index].color } as LayerAPI.ILayerColor,
                    upperBound: index === rowNumberOfClasses - 1 ? "Max" : cb.upperBound ?? 0,
                    lowerBound: index === 0 ? "Min" : cb.lowerBound ?? 0,
                } as LayerAPI.IClassBreak;
            });
            setDlClassBreaks(rowClassBreaks);
        }
    }, [selectedRow, locationData, colorRamps]);

    const updateColorRamp = (colorRampGuid: string): void => {
        setSelectedColorRampGuid(colorRampGuid);
    };

    const updateNumClasses = (numberOfClasses: string): void => {
        setNumberOfClasses(Number(numberOfClasses));
    };

    const onSelectChange = (key) => (value) => {
        onRecordChange({
            ...draftRecord,
            ...(key ? { [key]: value } : {}),
        });
    };

    const getClassBreakValidationError = useCallback((): string | null => {
        if (dlClassBreaks.length <= 1) {
            return null;
        }

        const validClassbreaks = dlClassBreaks.every((classBreak, i) => {
            const isFirst = i === 0;
            const isLast = i === dlClassBreaks.length - 1;

            if (!isLast) {
                const upperBound = Number(classBreak.upperBound);
                if (!classBreak.upperBound || upperBound === 0 || Number.isNaN(upperBound)) {
                    return false;
                }
                if (i > 0) {
                    const prevUpperBound = Number(dlClassBreaks[i - 1].upperBound);
                    if (upperBound <= prevUpperBound) {
                        return false;
                    }
                }
            }

            if (!isFirst) {
                const lowerBound = Number(classBreak.lowerBound);
                const prevUpperBound = Number(dlClassBreaks[i - 1].upperBound);
                if (!classBreak.lowerBound || lowerBound === 0 || Number.isNaN(lowerBound)) {
                    return false;
                }
                if (lowerBound !== prevUpperBound) {
                    return false;
                }
            }
            return true;
        });

        if (!validClassbreaks) {
            return formatMessage(messages.classBreakValidationError);
        }

        return null;
    }, [dlClassBreaks, formatMessage]);

    const getSurfaceDefaultWarningMessage = useCallback(
        (errorCode: string | number) => {
            const normalizedCode = String(errorCode).trim();
            if (normalizedCode === PARENT_SURFACE_DEFAULT_ERROR_CODE) {
                return formatMessage(messages.surfaceDefaultParent);
            }
            return null;
        },
        [formatMessage]
    );

    const clearValidationErrorState = useCallback(() => {
        dispatch(resetApiResult());
    }, [dispatch]);

    const saveDefinedLegend = useCallback(
        async (mergeModel: DefinedLegendAPI.IMergeDefinedLegendRequest) => {
            if (selectedRow) {
                await updateDefinedLegend(mergeModel).unwrap();
                closePanel();
                return;
            }

            await addDefinedLegend(mergeModel).unwrap();
            closePanel();
        },
        [addDefinedLegend, closePanel, selectedRow, updateDefinedLegend]
    );

    const onSave = async () => {
        const classBreakError = getClassBreakValidationError();
        if (classBreakError) {
            setClassBreakValidationError(classBreakError);
            return;
        }

        const mergeModel = {
            ownerDefinedLegendGuid: draftRecord[model.PROPS_DEFINED_LEGEND_GUID],
            layerTypeName: draftRecord[model.PROPS_LAYER_TYPE_NAME],
            importAttributeGuid: draftRecord[model.PROPS_ATTRIBUTE_GUID],
            cropGuid: draftRecord[model.PROPS_CROP_GUID],
            cropPurposeGuid: draftRecord[model.PROPS_CROP_PURPOSE_GUID],
            productGuid: draftRecord[model.PROPS_PRODUCT_GUID],
            orgLevelGuids: selectedLocations.map(
                (location) => location[model.PROPS_ORG_LEVEL_GUID]
            ),
            numberOfClasses: numberOfClasses ? Number(numberOfClasses) : null,
            colorRampGuid: selectedColorRampGuid,
            classBreaks: dlClassBreaks.map((cb, i) => ({
                classId: cb.classId,
                upperBound: i == dlClassBreaks.length - 1 ? 99999999 : cb.upperBound,
                lowerBound: i === 0 ? 0 : cb.lowerBound,
            })),
            createSurfaceDefault: createSurfaceDefault,
            displayTypeGuid: draftRecord[model.PROPS_DISPLAY_TYPE_GUID_ATTR],
            isEdit: Boolean(selectedRow),
        } as unknown as DefinedLegendAPI.IMergeDefinedLegendRequest;

        const shouldValidateSurfaceDefault =
            Boolean(selectedRow) || (!selectedRow && !createSurfaceDefault);
        if (!shouldValidateSurfaceDefault) {
            await saveDefinedLegend(mergeModel);
            return;
        }

        pendingDefinedLegendModelRef.current = mergeModel;

        try {
            clearValidationErrorState();
            await validateDefinedLegend(mergeModel).unwrap();
            await saveDefinedLegend(mergeModel);
        } catch (error) {
            clearValidationErrorState();

            const errorCodes = Array.isArray((error as any)?.data?.errors)
                ? (error as any).data.errors.map((e: any) => e.code)
                : [];

            const matchingErrorCode = errorCodes.find((code) => {
                const normalizedCode = String(code).trim();
                return normalizedCode === PARENT_SURFACE_DEFAULT_ERROR_CODE;
            });

            if (matchingErrorCode == null) {
                setSurfaceDefaultValidationDialog({ isOpen: false, message: "" });
                return;
            }

            setSurfaceDefaultValidationDialog({
                isOpen: true,
                message: getSurfaceDefaultWarningMessage(matchingErrorCode),
            });
        }
    };

    const isLoading =
        isLocationLoading ||
        cropDataIsLoading ||
        cropPurposeDataIsLoading ||
        isAddLoading ||
        isUpdateLoading ||
        attributeIsLoading ||
        isProductListLoading ||
        colorRamps.length === 0 ||
        allProducts.length === 0 ||
        numberOfClassesOptions.length === 0 ||
        displayTypesLoading ||
        isValidateLoading;

    const addEditPanel = () => (
        <div className="add-edit-panel defined-legend-add-edit-panel">
            {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE} />}
            <div className="section-container">
                <Section testId="defined-legend-details-section">
                    <SubSection testId="defined-legend-layer-type-subsection">
                        <SelectInput
                            tabIndex={0}
                            maxLength={50}
                            clearable={true}
                            options={layerTypeOptions}
                            placeholderText={formatMessage(messages.layerType)}
                            required
                            value={draftRecord[model.PROPS_LAYER_TYPE_NAME]}
                            onChange={onSelectChange(model.PROPS_LAYER_TYPE_NAME)}
                            testId="defined-legend-layer-type-select"
                        />
                        <SelectInput
                            clearable={true}
                            disabled={!draftRecord[model.PROPS_LAYER_TYPE_NAME]}
                            options={attributeOptions}
                            placeholderText={formatMessage(messages.attributeName)}
                            required
                            value={draftRecord[model.PROPS_ATTRIBUTE_GUID]}
                            onChange={onSelectChange(model.PROPS_ATTRIBUTE_GUID)}
                            testId="defined-legend-attribute-select"
                        />
                    </SubSection>
                    {isApplicationLayerType ? (
                        <SubSection testId="defined-legend-product-subsection">
                            <SelectInput
                                clearable={true}
                                hasError={isSelectedProductUnlinked}
                                optionIsHiddenKey={IS_SELECTABLE}
                                options={productOptions}
                                placeholderText={formatMessage(messages.productName)}
                                required
                                toolTip={productNotLinkedText}
                                value={draftRecord[model.PROPS_PRODUCT_GUID]}
                                onChange={onSelectChange(model.PROPS_PRODUCT_GUID)}
                                testId="defined-legend-product-select"
                            />
                        </SubSection>
                    ) : null}
                    {!isApplicationLayerType ? (
                        <SubSection testId="defined-legend-crop-subsection">
                            <SelectInput
                                clearable={true}
                                options={cropOptions}
                                placeholderText={formatMessage(messages.crop)}
                                required
                                value={draftRecord[model.PROPS_CROP_GUID]}
                                onChange={onSelectChange(model.PROPS_CROP_GUID)}
                                testId="defined-legend-crop-select"
                            />
                            <SelectInput
                                clearable={true}
                                options={cropPurposeOptions}
                                placeholderText={formatMessage(messages.cropPurposeName)}
                                value={draftRecord[model.PROPS_CROP_PURPOSE_GUID]}
                                onChange={onSelectChange(model.PROPS_CROP_PURPOSE_GUID)}
                                testId="defined-legend-crop-purpose-select"
                            />
                        </SubSection>
                    ) : null}
                    <SubSection testId="defined-legend-color-ramp-and-classes-subsection">
                        <SelectInput
                            required
                            inputCSS={{ backgroundImage: backgroundImageStyle }}
                            clearable={false}
                            filterable={false}
                            onChange={(e) => updateColorRamp(e)}
                            options={colorRampSelectOptions}
                            optionRenderer={colorOptionRenderer}
                            placeholderText={formatMessage(messages.colorRamp)}
                            value={selectedColorRampGuid}
                            testId="defined-legend-color-ramp-select"
                        />
                        <SelectInput
                            required
                            clearable={true}
                            filterable={false}
                            onChange={(e) => updateNumClasses(e)}
                            optionIsHiddenKey={ACTIVE_YN}
                            options={numClassesOptions}
                            placeholderText={formatMessage(messages.numberOfClasses)}
                            value={numberOfClasses?.toString()}
                            testId="defined-legend-number-of-classes-select"
                        />
                    </SubSection>
                    {isAdd ? (
                        <SubSection testId="defined-legend-surface-default-subsection">
                            <Checkbox
                                testId="defined-legend-surface-default-checkbox"
                                label={formatMessage(messages.surfaceDefault)}
                                onChange={(e, value) => setCreateSurfaceDefault(value)}
                                value={createSurfaceDefault}
                            />
                            {createSurfaceDefault ? (
                                <SelectInput
                                    testId="defined-legend-display-type-select"
                                    options={displayTypeOptions.filter((o) =>
                                        isRec
                                            ? o.label.toLowerCase().includes("rec")
                                            : !o.label.toLowerCase().includes("rec")
                                    )}
                                    value={draftRecord[model.PROPS_DISPLAY_TYPE_GUID_ATTR]}
                                    onChange={onSelectChange(model.PROPS_DISPLAY_TYPE_GUID_ATTR)}
                                    placeholderText={formatMessage(messages.displayTypeName)}
                                    clearable={false}
                                    required
                                />
                            ) : null}
                        </SubSection>
                    ) : null}
                    <SubSection
                        className="color-ramp-section"
                        testId="defined-legend-legend-preview-subsection"
                    >
                        <div className="legend-preview">{legendPreviewItems}</div>
                    </SubSection>
                </Section>
                <span className="bar section-fence" />
                <Section
                    required
                    className="grid-section"
                    headerText={formatMessage(messages.locationAffiliations)}
                    testId="defined-legend-location-affiliations-section"
                >
                    <OrgLevelList
                        orgLevels={locationOptions}
                        onSelectionChange={(items) => {
                            setTimeout(() => setSelectedLocations(items), 0);
                        }}
                        selectedOrgLevels={selectedLocations}
                    />
                </Section>
            </div>
            <DialogBox
                draggable={true}
                className="delete-confirmation-modal"
                footerType={DialogBoxFooterType.NONE}
                isOpen={classBreakValidationError !== null}
                onClose={() => {
                    setClassBreakValidationError(null);
                }}
                title={formatMessage(messages.classBreakValidationErrorTitle)}
                testId="defined-legend-class-break-validation-error-modal"
            >
                <p>{formatMessage(messages.classBreakValidationError)}</p>
            </DialogBox>
            <DialogBox
                draggable={true}
                className="confirmation-modal"
                footerType={DialogBoxFooterType.YES_NO}
                isOpen={surfaceDefaultValidationDialog.isOpen}
                onAction={async () => {
                    setSurfaceDefaultValidationDialog({ isOpen: false, message: "" });
                    const pendingModel = pendingDefinedLegendModelRef.current;
                    if (!pendingModel) {
                        return;
                    }
                    await saveDefinedLegend(pendingModel);
                }}
                onClose={() => {
                    setSurfaceDefaultValidationDialog({ isOpen: false, message: "" });
                }}
                title={formatMessage(messages.confirmTitle)}
                testId="defined-legend-confirmation-modal"
            >
                <p>{surfaceDefaultValidationDialog.message}</p>
            </DialogBox>
        </div>
    );

    const addEditPanelRendererRef = useRef<() => JSX.Element>(() => <></>);
    addEditPanelRendererRef.current = addEditPanel;

    const SlidingPanelContent = useMemo(() => () => addEditPanelRendererRef.current(), []);

    const checkIfRecordValid = useMemo(() => {
        if (selectedLocations.length === 0) {
            return true;
        }
        if (!draftRecord[model.PROPS_LAYER_TYPE_NAME] || !draftRecord[model.PROPS_ATTRIBUTE_GUID]) {
            return true;
        }
        if (isApplicationLayerType && !draftRecord[model.PROPS_PRODUCT_GUID]) {
            return true;
        }
        if (isApplicationLayerType && isSelectedProductUnlinked) {
            return true;
        }
        if (!isApplicationLayerType && !draftRecord[model.PROPS_CROP_GUID]) {
            return true;
        }
        if (!selectedColorRampGuid || !numberOfClasses) {
            return true;
        }
        if (createSurfaceDefault && !draftRecord[model.PROPS_DISPLAY_TYPE_GUID_ATTR]) {
            return true;
        }
        return false;
    }, [
        isApplicationLayerType,
        isSelectedProductUnlinked,
        selectedLocations,
        draftRecord,
        selectedColorRampGuid,
        numberOfClasses,
    ]);

    useEffect(() => {
        if (classBreakValidationError) {
            setClassBreakValidationError(null);
        }
    }, [dlClassBreaks]);

    return (
        <SlidingPanel
            component={SlidingPanelContent}
            navigateTo={{ parentNameCode: "101", childNameCode: "278" }}
        >
            <Button
                type={ButtonTypes.button}
                value="save"
                forceSubmit
                disabled={checkIfRecordValid || isLoading}
                onClick={onSave}
                testId="defined-legend-save-button"
            />
            <Button
                type={ButtonTypes.cancel}
                className="user-cancel"
                onClick={closePanel}
                testId="defined-legend-cancel-button"
            />
        </SlidingPanel>
    );
};

export default DefinedLegendAddEditPanel;
